import type { TableNode } from '../../stores/table-store';
import { isGroupRow } from '../../stores/utils/group-row-guards';
import type { HealthIssue } from '../../stores/utils/solution-health';
import type { CanvasEdge } from './canvas-edges-model';

/**
 * The facts "Explain this agent" shows as they are — counted, not guessed:
 * which agents route into this one and how, where it goes next, and which of
 * the health check's issues sit on its nodes. The prose around them is the
 * model's (`server-only/ai/explain-agent.ts`).
 */

/** Every route between this agent and one other, in one direction. */
export interface AgentRoute {
  /** The other agent's id and name. */
  agentId: string;
  agentTitle: string;
  /** The kinds among them, for the line's colour: intent, flow, error. */
  kinds: CanvasEdge['kind'][];
  /** Their routing labels — intents, or outcomes like `error` — once each. */
  labels: string[];
  /** Every one matched from example phrases rather than a stored route. */
  inferred: boolean;
}

/** One line per other agent: a solution routes to the same agent many ways. */
const groupRoutes = (
  edges: CanvasEdge[],
  end: 'source' | 'target',
  titles: Map<string, string>
): AgentRoute[] => {
  const byAgent = new Map<string, AgentRoute>();
  edges.forEach((e) => {
    const id = e[end];
    const r = byAgent.get(id) ?? {
      agentId: id,
      agentTitle: titles.get(id) || 'Untitled agent',
      kinds: [],
      labels: [],
      inferred: true,
    };
    if (!r.kinds.includes(e.kind)) r.kinds.push(e.kind);
    const label = e.label || (e.kind === 'flow' ? 'continues' : '');
    if (label && !r.labels.includes(label)) r.labels.push(label);
    r.inferred = r.inferred && !!e.inferred;
    byAgent.set(id, r);
  });
  return [...byAgent.values()];
};

export interface AgentFacts {
  members: TableNode[];
  incoming: AgentRoute[];
  outgoing: AgentRoute[];
  issues: HealthIssue[];
}

export const agentFacts = ({
  rows,
  agentId,
  edges,
  titles,
  issues,
}: {
  rows: TableNode[];
  agentId: string;
  edges: CanvasEdge[];
  /** Agent id → its name, for the other end of each route. */
  titles: Map<string, string>;
  issues: HealthIssue[];
}): AgentFacts => {
  const members = rows.filter(
    (r) => !isGroupRow(r) && r.settings?.groupId === agentId
  );
  const memberIds = new Set(members.map((r) => r.id));
  return {
    members,
    incoming: groupRoutes(
      edges.filter((e) => e.target === agentId && e.source !== agentId),
      'source',
      titles
    ),
    outgoing: groupRoutes(
      edges.filter((e) => e.source === agentId && e.target !== agentId),
      'target',
      titles
    ),
    issues: issues.filter((i) => i.rows.some((r) => memberIds.has(r.rowId))),
  };
};

/** Labels shown on one line before "and N more". */
const LABELS_SHOWN = 6;

/** "Escalation · error, stop, change", the way the panel and the prompt say it. */
export const routeLine = (r: AgentRoute) => {
  const shown = r.labels.slice(0, LABELS_SHOWN).join(', ');
  const more = r.labels.length - LABELS_SHOWN;
  return [
    r.agentTitle,
    shown ? `· ${shown}${more > 0 ? ` and ${more} more` : ''}` : '',
    r.inferred ? '(matched by example phrases)' : '',
  ]
    .filter(Boolean)
    .join(' ');
};
